import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Save, Trash2, Upload, Video, Image as ImageIcon, X,
  Eye, EyeOff, ChevronUp, ChevronDown, Star, Send,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = window.location.origin + '/api';

const EMPTY = {
  title: '',
  subtitle: '',
  cta_label: '',
  cta_link: '',
  gradient_from: '#0A1F44',
  gradient_to: '#1E40AF',
  accent: '#C8FF00',
  sponsored_by: '',
  image_url: '',
  video_url: '',
  supermarket_id: '',
  active: true,
  order: 0,
  slot_type: 'standard',
  premium_requested: false,
};

export default function AdminPromos() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'admin';
  const isStoreAdmin = user?.role === 'store_admin';

  const [promos, setPromos] = useState([]);
  const [impressions, setImpressions] = useState({}); // { promo_id: {impressions, unique_viewers} }
  const [supermarkets, setSupermarkets] = useState([]);
  const [editing, setEditing] = useState(null); // full promo object or {...EMPTY, __new: true}
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVid, setUploadingVid] = useState(false);
  const imgInputRef = useRef(null);
  const vidInputRef = useRef(null);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: p }, { data: s }, { data: imp }] = await Promise.all([
        axios.get(`${API}/admin/promos`, { withCredentials: true }),
        axios.get(`${API}/supermarkets`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/promos/impressions?days=7`, { withCredentials: true }).catch(() => ({ data: { totals: {} } })),
      ]);
      setPromos(Array.isArray(p) ? p : []);
      setSupermarkets(Array.isArray(s) ? s : []);
      setImpressions(imp?.totals || {});
    } catch (err) {
      console.error('Load promos failed', err);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const startNew = () => {
    const nextOrder = promos.length ? Math.max(...promos.map(p => p.order || 0)) + 1 : 1;
    setEditing({
      ...EMPTY,
      order: nextOrder,
      supermarket_id: isStoreAdmin ? (user?.assigned_supermarket_id || '') : '',
      __new: true,
    });
  };

  const startEdit = (promo) => setEditing({ ...promo });

  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing?.title?.trim()) {
      alert('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        subtitle: editing.subtitle || '',
        cta_label: editing.cta_label || '',
        cta_link: editing.cta_link || '',
        gradient_from: editing.gradient_from || '#0A1F44',
        gradient_to: editing.gradient_to || '#1E40AF',
        accent: editing.accent || '#C8FF00',
        sponsored_by: editing.sponsored_by || '',
        image_url: editing.image_url || '',
        video_url: editing.video_url || '',
        supermarket_id: isStoreAdmin ? (user?.assigned_supermarket_id || '') : (editing.supermarket_id || ''),
        active: !!editing.active,
        order: Number(editing.order) || 0,
        slot_type: editing.slot_type === 'premium' ? 'premium' : 'standard',
        premium_requested: !!editing.premium_requested,
      };
      if (editing.__new) {
        await axios.post(`${API}/admin/promos`, payload, { withCredentials: true });
      } else {
        await axios.put(`${API}/admin/promos/${editing.id}`, payload, { withCredentials: true });
      }
      await fetchAll();
      setEditing(null);
    } catch (err) {
      console.error('Save promo failed', err);
      alert(err?.response?.data?.detail || 'Could not save promo');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (promo) => {
    if (!window.confirm(`Delete "${promo.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/promos/${promo.id}`, { withCredentials: true });
      await fetchAll();
    } catch (err) {
      console.error('Delete promo failed', err);
      alert(err?.response?.data?.detail || 'Could not delete promo');
    }
  };

  const toggleActive = async (promo) => {
    try {
      const payload = { ...promo, active: !promo.active };
      delete payload.__new;
      await axios.put(`${API}/admin/promos/${promo.id}`, payload, { withCredentials: true });
      await fetchAll();
    } catch (err) {
      console.error('Toggle failed', err);
    }
  };

  const move = async (index, dir) => {
    const swapWith = index + dir;
    if (swapWith < 0 || swapWith >= promos.length) return;
    const reordered = [...promos];
    [reordered[index], reordered[swapWith]] = [reordered[swapWith], reordered[index]];
    setPromos(reordered);
    try {
      await axios.post(`${API}/admin/promos/reorder`,
        { ids: reordered.map(p => p.id) },
        { withCredentials: true });
    } catch (err) {
      console.error('Reorder failed', err);
      await fetchAll(); // revert
    }
  };

  const upload = async (file, kind) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const setBusy = kind === 'video' ? setUploadingVid : setUploadingImg;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/promos/upload?kind=${kind}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setEditing(cur => ({ ...cur, [kind === 'video' ? 'video_url' : 'image_url']: data.url }));
    } catch (err) {
      console.error('Upload failed', err);
      alert(err?.response?.data?.detail || `Could not upload ${kind}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-page" data-testid="admin-promos-page">
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Admin
      </button>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0 }} data-testid="admin-promos-title">Promo Slides</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '4px 0 0' }}>
            {isSuperAdmin
              ? 'Manage the home carousel. Set a background image, upload a promo video, or both.'
              : `Sponsored slides for ${user?.assigned_supermarket_name || 'your supermarket'}.`}
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="new-promo-btn"
          style={{ padding: '10px 16px', background: 'var(--lime)', color: 'var(--navy)', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}
        >
          <Plus size={16} /> New slide
        </button>
      </div>

      {/* Promo list */}
      <div style={{ display: 'grid', gap: 12, marginTop: 20 }} data-testid="promo-list">
        {promos.length === 0 && (
          <div style={{ padding: 22, background: '#F1F5F9', borderRadius: 12, color: 'var(--text-muted)', textAlign: 'center' }} data-testid="empty-promos">
            No promo slides yet. Click &quot;New slide&quot; to add one.
          </div>
        )}
        {promos.map((p, i) => {
          const hasMedia = !!(p.video_url || p.image_url);
          return (
            <div
              key={p.id}
              data-testid={`promo-row-${p.id}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '150px 1fr auto',
                gap: 14,
                padding: 12,
                background: 'white',
                border: '1px solid var(--border-gray)',
                borderRadius: 12,
                opacity: p.active ? 1 : 0.55,
                alignItems: 'center',
              }}
            >
              {/* Preview */}
              <div style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                background: `linear-gradient(135deg, ${p.gradient_from || '#0A1F44'}, ${p.gradient_to || '#1E40AF'})`,
                borderRadius: 8,
                overflow: 'hidden',
              }}>
                {p.video_url ? (
                  <video src={p.video_url} poster={p.image_url || undefined} muted playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : p.image_url ? (
                  <img src={p.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : null}
                {hasMedia && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(10,31,68,0.6), rgba(10,31,68,0.2))' }} />}
                <div style={{ position: 'absolute', left: 8, bottom: 6, color: p.accent || 'white', fontWeight: 700, fontSize: 12, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                  {p.title}
                </div>
                {p.video_url && (
                  <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: 10, padding: '2px 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Video size={10} /> Video
                  </div>
                )}
                {!p.video_url && p.image_url && (
                  <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: 10, padding: '2px 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ImageIcon size={10} /> Image
                  </div>
                )}
              </div>

              {/* Meta */}
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <strong style={{ color: 'var(--navy)' }}>{p.title || 'Untitled'}</strong>
                  {p.slot_type === 'premium' && (
                    <span data-testid={`premium-badge-${p.id}`} style={{ background: 'linear-gradient(135deg,#F59E0B,#F5D000)', color: '#1F2937', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Star size={10} fill="currentColor" /> Featured
                    </span>
                  )}
                  {p.premium_requested && p.slot_type !== 'premium' && (
                    <span data-testid={`premium-req-badge-${p.id}`} style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <Send size={10} /> Upgrade requested
                    </span>
                  )}
                  {!p.active && <span style={{ background: '#F1F5F9', color: 'var(--text-muted)', padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>Paused</span>}
                  {p.sponsored_by && <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>Sponsored · {p.sponsored_by}</span>}
                  {p.supermarket_name && <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>{p.supermarket_name}</span>}
                  {!p.supermarket_id && <span style={{ background: '#DCFCE7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>Platform-wide</span>}
                </div>
                {p.subtitle && <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 4 }}>{p.subtitle}</div>}
                <div style={{ display: 'flex', gap: 12, color: 'var(--text-muted)', fontSize: 12, flexWrap: 'wrap' }}>
                  {p.cta_label && <span>CTA: <strong style={{ color: 'var(--text-dark)' }}>{p.cta_label}</strong> → {p.cta_link || '—'}</span>}
                  <span>Order: {p.order || 0}</span>
                  <span data-testid={`impressions-${p.id}`} style={{ color: (impressions[p.id]?.impressions || 0) > 0 ? '#059669' : 'var(--text-muted)' }}>
                    👁 {impressions[p.id]?.impressions || 0} views · {impressions[p.id]?.unique_viewers || 0} unique
                  </span>
                  <span data-testid={`clicks-${p.id}`} style={{ color: (impressions[p.id]?.clicks || 0) > 0 ? '#DB2777' : 'var(--text-muted)' }}>
                    👆 {impressions[p.id]?.clicks || 0} clicks · CTR {impressions[p.id]?.ctr_percent || 0}%
                    <span style={{ opacity: 0.7 }}> · last 7d</span>
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => move(i, -1)} disabled={i === 0}
                    data-testid={`promo-up-${p.id}`}
                    title="Move up"
                    style={{ padding: 6, background: '#F1F5F9', border: 'none', borderRadius: 6, cursor: i === 0 ? 'not-allowed' : 'pointer' }}>
                    <ChevronUp size={14} />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === promos.length - 1}
                    data-testid={`promo-down-${p.id}`}
                    title="Move down"
                    style={{ padding: 6, background: '#F1F5F9', border: 'none', borderRadius: 6, cursor: i === promos.length - 1 ? 'not-allowed' : 'pointer' }}>
                    <ChevronDown size={14} />
                  </button>
                </div>
                <button onClick={() => toggleActive(p)}
                  data-testid={`promo-toggle-${p.id}`}
                  style={{ padding: '6px 10px', background: p.active ? '#F1F5F9' : '#DCFCE7', color: p.active ? 'var(--text-dark)' : '#166534', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  {p.active ? <><EyeOff size={12} /> Pause</> : <><Eye size={12} /> Activate</>}
                </button>
                <button onClick={() => startEdit(p)}
                  data-testid={`promo-edit-${p.id}`}
                  style={{ padding: '6px 10px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                  Edit
                </button>
                <button onClick={() => remove(p)}
                  data-testid={`promo-delete-${p.id}`}
                  style={{ padding: '6px 10px', background: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Editor modal */}
      {editing && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) cancelEdit(); }}
          data-testid="promo-editor-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16, zIndex: 200,
          }}
        >
          <div style={{ background: 'white', borderRadius: 14, maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, color: 'var(--navy)' }}>{editing.__new ? 'New promo slide' : 'Edit slide'}</h3>
              <button onClick={cancelEdit} data-testid="close-editor-btn" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            {/* Live preview */}
            <div
              data-testid="editor-preview"
              style={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 6',
                background: `linear-gradient(135deg, ${editing.gradient_from}, ${editing.gradient_to})`,
                borderRadius: 12,
                overflow: 'hidden',
                marginBottom: 16,
              }}
            >
              {editing.video_url ? (
                <video key={editing.video_url} src={editing.video_url} poster={editing.image_url || undefined}
                  muted autoPlay loop playsInline
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : editing.image_url ? (
                <img src={editing.image_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : null}
              {(editing.image_url || editing.video_url) && (
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(10,31,68,0.75), rgba(10,31,68,0.25))' }} />
              )}
              <div style={{ position: 'absolute', left: 16, bottom: 14, right: 16, color: editing.accent, textShadow: '0 2px 12px rgba(0,0,0,0.55)' }}>
                <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 4 }}>{editing.title || 'Slide title'}</div>
                {editing.subtitle && <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: 13 }}>{editing.subtitle}</div>}
              </div>
            </div>

            {/* Media pickers */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <MediaField
                label="Background image"
                kind="image"
                icon={<ImageIcon size={14} />}
                value={editing.image_url}
                onChange={(url) => setEditing(cur => ({ ...cur, image_url: url }))}
                onUpload={(f) => upload(f, 'image')}
                uploading={uploadingImg}
                inputRef={imgInputRef}
                accept="image/*"
                testid="image"
              />
              <MediaField
                label="Background video"
                kind="video"
                icon={<Video size={14} />}
                value={editing.video_url}
                onChange={(url) => setEditing(cur => ({ ...cur, video_url: url }))}
                onUpload={(f) => upload(f, 'video')}
                uploading={uploadingVid}
                inputRef={vidInputRef}
                accept="video/*"
                testid="video"
              />
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Title *">
                <input value={editing.title} onChange={e => setEditing(cur => ({ ...cur, title: e.target.value }))}
                  data-testid="promo-title-input"
                  style={inputStyle} maxLength={80} />
              </Field>
              <Field label="Subtitle">
                <input value={editing.subtitle} onChange={e => setEditing(cur => ({ ...cur, subtitle: e.target.value }))}
                  data-testid="promo-subtitle-input"
                  style={inputStyle} maxLength={140} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="CTA label">
                  <input value={editing.cta_label} onChange={e => setEditing(cur => ({ ...cur, cta_label: e.target.value }))}
                    data-testid="promo-cta-label-input"
                    style={inputStyle} placeholder="Shop Now" />
                </Field>
                <Field label="CTA link" hint="/path, https://…, or 'whatsapp'">
                  <input value={editing.cta_link} onChange={e => setEditing(cur => ({ ...cur, cta_link: e.target.value }))}
                    data-testid="promo-cta-link-input"
                    style={inputStyle} placeholder="/category/fruits" />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <Field label="Gradient from">
                  <input type="color" value={editing.gradient_from} onChange={e => setEditing(cur => ({ ...cur, gradient_from: e.target.value }))}
                    data-testid="promo-grad-from" style={{ ...inputStyle, padding: 4, height: 36 }} />
                </Field>
                <Field label="Gradient to">
                  <input type="color" value={editing.gradient_to} onChange={e => setEditing(cur => ({ ...cur, gradient_to: e.target.value }))}
                    data-testid="promo-grad-to" style={{ ...inputStyle, padding: 4, height: 36 }} />
                </Field>
                <Field label="Accent (text)">
                  <input type="color" value={editing.accent} onChange={e => setEditing(cur => ({ ...cur, accent: e.target.value }))}
                    data-testid="promo-accent" style={{ ...inputStyle, padding: 4, height: 36 }} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Sponsored by" hint="Optional — shows a badge">
                  <input value={editing.sponsored_by} onChange={e => setEditing(cur => ({ ...cur, sponsored_by: e.target.value }))}
                    data-testid="promo-sponsor-input"
                    style={inputStyle} placeholder="e.g. Eliseo Group" />
                </Field>
                <Field label="Sort order">
                  <input type="number" value={editing.order} onChange={e => setEditing(cur => ({ ...cur, order: e.target.value }))}
                    data-testid="promo-order-input"
                    style={inputStyle} />
                </Field>
              </div>

              {isSuperAdmin && (
                <Field label="Scope" hint="Choose a supermarket, or leave blank for platform-wide">
                  <select value={editing.supermarket_id || ''} onChange={e => setEditing(cur => ({ ...cur, supermarket_id: e.target.value }))}
                    data-testid="promo-scope-select"
                    style={inputStyle}>
                    <option value="">Platform-wide</option>
                    {supermarkets.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </Field>
              )}

              {isSuperAdmin && (
                <Field label="Slot type" hint="Premium slots sort ahead of standard on the carousel and appear with a gold Featured badge.">
                  <select value={editing.slot_type || 'standard'} onChange={e => setEditing(cur => ({ ...cur, slot_type: e.target.value }))}
                    data-testid="promo-slot-type-select"
                    style={inputStyle}>
                    <option value="standard">Standard slot</option>
                    <option value="premium">Premium slot ★ Featured (paid add-on)</option>
                  </select>
                </Field>
              )}

              {isStoreAdmin && (
                <div style={{ padding: 12, background: editing.slot_type === 'premium' ? '#FEF3C7' : '#F8FAFC', borderRadius: 8, border: '1px solid var(--border-gray)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Star size={12} fill={editing.slot_type === 'premium' ? '#F59E0B' : 'none'} />
                    {editing.slot_type === 'premium' ? 'Featured slot (Premium)' : 'Standard slot'}
                  </div>
                  {editing.slot_type === 'premium' ? (
                    <div style={{ fontSize: 11, color: '#92400E' }}>Your premium upgrade is live. Contact the platform admin to downgrade.</div>
                  ) : (
                    <>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                        Upgrade to a Featured slot for priority position + gold badge. A platform admin will approve and add the fee to your monthly bill.
                      </div>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                        <input type="checkbox" checked={!!editing.premium_requested}
                          onChange={e => setEditing(cur => ({ ...cur, premium_requested: e.target.checked }))}
                          data-testid="promo-request-premium" />
                        {editing.premium_requested ? 'Premium upgrade requested — pending admin review' : 'Request premium upgrade'}
                      </label>
                    </>
                  )}
                </div>
              )}

              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-dark)' }}>
                <input type="checkbox" checked={!!editing.active} onChange={e => setEditing(cur => ({ ...cur, active: e.target.checked }))}
                  data-testid="promo-active-check" />
                Active (visible on carousel)
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button onClick={cancelEdit}
                data-testid="cancel-edit-btn"
                style={{ padding: '10px 16px', background: '#F1F5F9', color: 'var(--text-dark)', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                data-testid="save-promo-btn"
                style={{ padding: '10px 16px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: saving ? 0.6 : 1 }}>
                <Save size={14} /> {saving ? 'Saving…' : 'Save slide'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  border: '1px solid var(--border-gray)',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

function Field({ label, hint, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{hint}</div>}
    </label>
  );
}

function MediaField({ label, kind, icon, value, onChange, onUpload, uploading, inputRef, accept, testid }) {
  return (
    <div style={{ padding: 10, background: '#F8FAFC', borderRadius: 10, border: '1px solid var(--border-gray)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
        {icon} {label}
      </div>
      <input
        type="text"
        value={value || ''}
        placeholder={kind === 'video' ? 'Paste video URL' : 'Paste image URL'}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testid}-url-input`}
        style={{ ...inputStyle, marginBottom: 6 }}
      />
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-upload-btn`}
          style={{ flex: 1, padding: '8px 10px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 6, cursor: uploading ? 'wait' : 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontWeight: 600, opacity: uploading ? 0.6 : 1 }}
        >
          <Upload size={12} /> {uploading ? 'Uploading…' : `Upload ${kind}`}
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            data-testid={`${testid}-clear-btn`}
            style={{ padding: '8px 10px', background: '#FEE2E2', color: '#B91C1C', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
          >
            Clear
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUpload(f);
          e.target.value = '';
        }}
      />
      {kind === 'video' && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          MP4/WebM up to 30 MB. Autoplay, muted, looped.
        </div>
      )}
      {kind === 'image' && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          JPG/PNG/WebP up to 5 MB. Doubles as video poster.
        </div>
      )}
    </div>
  );
}
